/**
 * Shared IATA airport reference for the Nexa platform.
 *
 * The values below are SEEDED from the LATAM-group network slice (the same set
 * the ops simulator uses), but this package is intentionally self-contained —
 * it does NOT import `tools/latam-ops-sim`. Runtime code depends on this
 * package, never on the sim tool. New airports/regions are added here.
 *
 * `region` is a coarse grouping token (country-cluster or continent bucket),
 * `country` is ISO 3166-1 alpha-3.
 *
 * `tz` is the airport's **IANA time-zone id** and is NOT optional. Airline
 * manifests quote departure/arrival times in *airport-local* wall-clock, while
 * the canonical model stores `scheduled_departure_utc` — and that instant is
 * baked into the flight URN by `URN.flight({ code, departureAt })`. A wrong or
 * missing zone therefore mints a *wrong flight URN*, silently detaching the
 * case from the rest of the platform. Because a plausible-but-wrong instant is
 * worse than a crash, the field is required (a new airport without a zone is a
 * compile error) and the zone must be the **canonical primary** IANA name, not
 * a deprecated link and never `UTC`/`Etc/*`.
 *
 * Note the zone is a per-airport property, not a per-country one:
 *   - CHL: `America/Santiago` observes DST, but Magallanes (PUQ) is on
 *     permanent UTC−3 (`America/Punta_Arenas`) and Easter Island (IPC) is
 *     `Pacific/Easter` — three zones in one country.
 *   - BRA: Manaus (MAO) is UTC−4 (`America/Manaus`) while São Paulo is UTC−3,
 *     and the north-east (NAT) is `America/Fortaleza` — same UTC−3 offset as
 *     São Paulo today, but a different zone with a different DST history.
 */

export type RegionCode =
  | 'CL'
  | 'PE'
  | 'BR'
  | 'CO'
  | 'AR'
  | 'UY'
  | 'PY'
  | 'EC'
  | 'VE'
  | 'EU'
  | 'NA'
  | 'OC'
  | 'ME'
  | 'AS'
  | 'AF'
  | 'CB';

export interface AirportRef {
  /** IATA code (3 letters, uppercase). */
  iata: string;
  /** Friendly city / airport name. */
  city: string;
  /** ISO 3166-1 alpha-3 country code. */
  country: string;
  /** Coarse region grouping. */
  region: RegionCode;
  /**
   * Canonical IANA time-zone id for the airport, e.g. `'America/Santiago'`.
   * Required — see the module doc for why a missing zone must not compile.
   */
  tz: string;
}

/**
 * Canonical airport catalog, keyed by IATA. Seeded values; extend as the
 * platform onboards more of the IATA universe.
 */
export const AIRPORTS: Readonly<Record<string, AirportRef>> = Object.freeze({
  // ── Chile ──────────────────────────────────────────────────────────────
  // Mainland Chile observes DST (UTC−4 winter / UTC−3 summer). Magallanes and
  // Easter Island are separate zones — see the module doc.
  SCL: { iata: 'SCL', city: 'Santiago', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  ANF: { iata: 'ANF', city: 'Antofagasta', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  CJC: { iata: 'CJC', city: 'Calama', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  IQQ: { iata: 'IQQ', city: 'Iquique', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  LSC: { iata: 'LSC', city: 'La Serena', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  CCP: { iata: 'CCP', city: 'Concepción', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  PMC: { iata: 'PMC', city: 'Puerto Montt', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  ARI: { iata: 'ARI', city: 'Arica', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  CPO: { iata: 'CPO', city: 'Copiapó', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  ZCO: { iata: 'ZCO', city: 'Temuco', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  ZAL: { iata: 'ZAL', city: 'Valdivia', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  ZOS: { iata: 'ZOS', city: 'Osorno', country: 'CHL', region: 'CL', tz: 'America/Santiago' },
  // Aysén: permanent UTC−3 since 2025 (IANA 2025a split it out of Santiago).
  BBA: { iata: 'BBA', city: 'Balmaceda', country: 'CHL', region: 'CL', tz: 'America/Coyhaique' },
  // Magallanes: permanent UTC−3 since 2017, no DST.
  PUQ: { iata: 'PUQ', city: 'Punta Arenas', country: 'CHL', region: 'CL', tz: 'America/Punta_Arenas' },
  // Easter Island: UTC−6 standard / UTC−5 summer.
  IPC: { iata: 'IPC', city: 'Easter Island', country: 'CHL', region: 'CL', tz: 'Pacific/Easter' },

  // ── Peru ───────────────────────────────────────────────────────────────
  // Single zone, permanent UTC−5, no DST.
  LIM: { iata: 'LIM', city: 'Lima', country: 'PER', region: 'PE', tz: 'America/Lima' },
  CUZ: { iata: 'CUZ', city: 'Cusco', country: 'PER', region: 'PE', tz: 'America/Lima' },
  AQP: { iata: 'AQP', city: 'Arequipa', country: 'PER', region: 'PE', tz: 'America/Lima' },
  TRU: { iata: 'TRU', city: 'Trujillo', country: 'PER', region: 'PE', tz: 'America/Lima' },
  PIU: { iata: 'PIU', city: 'Piura', country: 'PER', region: 'PE', tz: 'America/Lima' },
  IQT: { iata: 'IQT', city: 'Iquitos', country: 'PER', region: 'PE', tz: 'America/Lima' },
  // SKY (H2) Peru domestic stations. A SKY test manifest (2026-10-30) flew TPP / PCL /
  // TBP feeders into LIM–CUZ and PEM onward legs; with no row here the compiler could
  // not resolve their zone and refused the import (UNKNOWN_AIRPORT).
  CIX: { iata: 'CIX', city: 'Chiclayo', country: 'PER', region: 'PE', tz: 'America/Lima' },
  TPP: { iata: 'TPP', city: 'Tarapoto', country: 'PER', region: 'PE', tz: 'America/Lima' },
  PCL: { iata: 'PCL', city: 'Pucallpa', country: 'PER', region: 'PE', tz: 'America/Lima' },
  TBP: { iata: 'TBP', city: 'Tumbes', country: 'PER', region: 'PE', tz: 'America/Lima' },
  CJA: { iata: 'CJA', city: 'Cajamarca', country: 'PER', region: 'PE', tz: 'America/Lima' },
  JUL: { iata: 'JUL', city: 'Juliaca', country: 'PER', region: 'PE', tz: 'America/Lima' },
  TCQ: { iata: 'TCQ', city: 'Tacna', country: 'PER', region: 'PE', tz: 'America/Lima' },
  AYP: { iata: 'AYP', city: 'Ayacucho', country: 'PER', region: 'PE', tz: 'America/Lima' },
  PEM: { iata: 'PEM', city: 'Puerto Maldonado', country: 'PER', region: 'PE', tz: 'America/Lima' },

  // ── Brazil ─────────────────────────────────────────────────────────────
  // Brazil dropped DST in 2019; the zones still differ historically and MAO
  // is a full hour behind the south-east.
  GRU: { iata: 'GRU', city: 'São Paulo', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  CGH: { iata: 'CGH', city: 'São Paulo', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  GIG: { iata: 'GIG', city: 'Rio de Janeiro', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  SDU: { iata: 'SDU', city: 'Rio de Janeiro', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  BSB: { iata: 'BSB', city: 'Brasília', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  CNF: { iata: 'CNF', city: 'Belo Horizonte', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  SSA: { iata: 'SSA', city: 'Salvador', country: 'BRA', region: 'BR', tz: 'America/Bahia' },
  REC: { iata: 'REC', city: 'Recife', country: 'BRA', region: 'BR', tz: 'America/Recife' },
  FOR: { iata: 'FOR', city: 'Fortaleza', country: 'BRA', region: 'BR', tz: 'America/Fortaleza' },
  POA: { iata: 'POA', city: 'Porto Alegre', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  CWB: { iata: 'CWB', city: 'Curitiba', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  FLN: { iata: 'FLN', city: 'Florianópolis', country: 'BRA', region: 'BR', tz: 'America/Sao_Paulo' },
  // Amazonas: UTC−4.
  MAO: { iata: 'MAO', city: 'Manaus', country: 'BRA', region: 'BR', tz: 'America/Manaus' },

  // ── Colombia ───────────────────────────────────────────────────────────
  // Single zone, permanent UTC−5, no DST.
  BOG: { iata: 'BOG', city: 'Bogotá', country: 'COL', region: 'CO', tz: 'America/Bogota' },
  MDE: { iata: 'MDE', city: 'Medellín', country: 'COL', region: 'CO', tz: 'America/Bogota' },
  CLO: { iata: 'CLO', city: 'Cali', country: 'COL', region: 'CO', tz: 'America/Bogota' },
  CTG: { iata: 'CTG', city: 'Cartagena', country: 'COL', region: 'CO', tz: 'America/Bogota' },
  BAQ: { iata: 'BAQ', city: 'Barranquilla', country: 'COL', region: 'CO', tz: 'America/Bogota' },

  // ── Argentina / Uruguay / Paraguay / Ecuador ───────────────────────────
  // Argentina is one offset (UTC−3, no DST) split across per-province IANA
  // zones. Bariloche is in Río Negro, which tzdata groups under
  // `America/Argentina/Salta` (zone covers SA, LP, NQ, RN).
  EZE: { iata: 'EZE', city: 'Buenos Aires', country: 'ARG', region: 'AR', tz: 'America/Argentina/Buenos_Aires' },
  AEP: { iata: 'AEP', city: 'Buenos Aires', country: 'ARG', region: 'AR', tz: 'America/Argentina/Buenos_Aires' },
  COR: { iata: 'COR', city: 'Córdoba', country: 'ARG', region: 'AR', tz: 'America/Argentina/Cordoba' },
  MDZ: { iata: 'MDZ', city: 'Mendoza', country: 'ARG', region: 'AR', tz: 'America/Argentina/Mendoza' },
  BRC: { iata: 'BRC', city: 'Bariloche', country: 'ARG', region: 'AR', tz: 'America/Argentina/Salta' },
  MVD: { iata: 'MVD', city: 'Montevideo', country: 'URY', region: 'UY', tz: 'America/Montevideo' },
  ASU: { iata: 'ASU', city: 'Asunción', country: 'PRY', region: 'PY', tz: 'America/Asuncion' },
  UIO: { iata: 'UIO', city: 'Quito', country: 'ECU', region: 'EC', tz: 'America/Guayaquil' },
  GYE: { iata: 'GYE', city: 'Guayaquil', country: 'ECU', region: 'EC', tz: 'America/Guayaquil' },

  // ── Long-haul: Europe / North America / Oceania ────────────────────────
  // These DO observe DST, on northern-hemisphere (EU/US) or southern
  // (AU/NZ) schedules — a fixed offset is wrong half the year.
  MAD: { iata: 'MAD', city: 'Madrid', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  LIS: { iata: 'LIS', city: 'Lisbon', country: 'PRT', region: 'EU', tz: 'Europe/Lisbon' },
  FCO: { iata: 'FCO', city: 'Rome', country: 'ITA', region: 'EU', tz: 'Europe/Rome' },
  MIA: { iata: 'MIA', city: 'Miami', country: 'USA', region: 'NA', tz: 'America/New_York' },
  JFK: { iata: 'JFK', city: 'New York', country: 'USA', region: 'NA', tz: 'America/New_York' },
  LAX: { iata: 'LAX', city: 'Los Angeles', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  MCO: { iata: 'MCO', city: 'Orlando', country: 'USA', region: 'NA', tz: 'America/New_York' },
  AKL: { iata: 'AKL', city: 'Auckland', country: 'NZL', region: 'OC', tz: 'Pacific/Auckland' },
  SYD: { iata: 'SYD', city: 'Sydney', country: 'AUS', region: 'OC', tz: 'Australia/Sydney' },

  // ── Etihad (EY) network — AUH hub, Gulf / subcontinent fan, long-haul gateways ──
  // Gulf states, India and most of Asia keep a fixed offset year-round; Europe,
  // North America and south-east Australia observe DST — hence IANA zones, not offsets.
  // Gulf / Middle East
  AUH: { iata: 'AUH', city: 'Abu Dhabi', country: 'ARE', region: 'ME', tz: 'Asia/Dubai' },
  DOH: { iata: 'DOH', city: 'Doha', country: 'QAT', region: 'ME', tz: 'Asia/Qatar' },
  BAH: { iata: 'BAH', city: 'Bahrain', country: 'BHR', region: 'ME', tz: 'Asia/Bahrain' },
  KWI: { iata: 'KWI', city: 'Kuwait', country: 'KWT', region: 'ME', tz: 'Asia/Kuwait' },
  MCT: { iata: 'MCT', city: 'Muscat', country: 'OMN', region: 'ME', tz: 'Asia/Muscat' },
  RUH: { iata: 'RUH', city: 'Riyadh', country: 'SAU', region: 'ME', tz: 'Asia/Riyadh' },
  JED: { iata: 'JED', city: 'Jeddah', country: 'SAU', region: 'ME', tz: 'Asia/Riyadh' },
  DMM: { iata: 'DMM', city: 'Dammam', country: 'SAU', region: 'ME', tz: 'Asia/Riyadh' },
  AMM: { iata: 'AMM', city: 'Amman', country: 'JOR', region: 'ME', tz: 'Asia/Amman' },
  BEY: { iata: 'BEY', city: 'Beirut', country: 'LBN', region: 'ME', tz: 'Asia/Beirut' },
  CAI: { iata: 'CAI', city: 'Cairo', country: 'EGY', region: 'ME', tz: 'Africa/Cairo' },
  // Asia
  BOM: { iata: 'BOM', city: 'Mumbai', country: 'IND', region: 'AS', tz: 'Asia/Kolkata' },
  DEL: { iata: 'DEL', city: 'Delhi', country: 'IND', region: 'AS', tz: 'Asia/Kolkata' },
  MAA: { iata: 'MAA', city: 'Chennai', country: 'IND', region: 'AS', tz: 'Asia/Kolkata' },
  HYD: { iata: 'HYD', city: 'Hyderabad', country: 'IND', region: 'AS', tz: 'Asia/Kolkata' },
  BLR: { iata: 'BLR', city: 'Bengaluru', country: 'IND', region: 'AS', tz: 'Asia/Kolkata' },
  COK: { iata: 'COK', city: 'Kochi', country: 'IND', region: 'AS', tz: 'Asia/Kolkata' },
  KHI: { iata: 'KHI', city: 'Karachi', country: 'PAK', region: 'AS', tz: 'Asia/Karachi' },
  LHE: { iata: 'LHE', city: 'Lahore', country: 'PAK', region: 'AS', tz: 'Asia/Karachi' },
  CMB: { iata: 'CMB', city: 'Colombo', country: 'LKA', region: 'AS', tz: 'Asia/Colombo' },
  DAC: { iata: 'DAC', city: 'Dhaka', country: 'BGD', region: 'AS', tz: 'Asia/Dhaka' },
  KTM: { iata: 'KTM', city: 'Kathmandu', country: 'NPL', region: 'AS', tz: 'Asia/Kathmandu' },
  MLE: { iata: 'MLE', city: 'Malé', country: 'MDV', region: 'AS', tz: 'Indian/Maldives' },
  BKK: { iata: 'BKK', city: 'Bangkok', country: 'THA', region: 'AS', tz: 'Asia/Bangkok' },
  KUL: { iata: 'KUL', city: 'Kuala Lumpur', country: 'MYS', region: 'AS', tz: 'Asia/Kuala_Lumpur' },
  SIN: { iata: 'SIN', city: 'Singapore', country: 'SGP', region: 'AS', tz: 'Asia/Singapore' },
  CGK: { iata: 'CGK', city: 'Jakarta', country: 'IDN', region: 'AS', tz: 'Asia/Jakarta' },
  MNL: { iata: 'MNL', city: 'Manila', country: 'PHL', region: 'AS', tz: 'Asia/Manila' },
  ICN: { iata: 'ICN', city: 'Seoul', country: 'KOR', region: 'AS', tz: 'Asia/Seoul' },
  NRT: { iata: 'NRT', city: 'Tokyo', country: 'JPN', region: 'AS', tz: 'Asia/Tokyo' },
  PVG: { iata: 'PVG', city: 'Shanghai', country: 'CHN', region: 'AS', tz: 'Asia/Shanghai' },
  // Europe
  IST: { iata: 'IST', city: 'Istanbul', country: 'TUR', region: 'EU', tz: 'Europe/Istanbul' },
  LHR: { iata: 'LHR', city: 'London', country: 'GBR', region: 'EU', tz: 'Europe/London' },
  MAN: { iata: 'MAN', city: 'Manchester', country: 'GBR', region: 'EU', tz: 'Europe/London' },
  DUB: { iata: 'DUB', city: 'Dublin', country: 'IRL', region: 'EU', tz: 'Europe/Dublin' },
  CDG: { iata: 'CDG', city: 'Paris', country: 'FRA', region: 'EU', tz: 'Europe/Paris' },
  FRA: { iata: 'FRA', city: 'Frankfurt', country: 'DEU', region: 'EU', tz: 'Europe/Berlin' },
  MUC: { iata: 'MUC', city: 'Munich', country: 'DEU', region: 'EU', tz: 'Europe/Berlin' },
  AMS: { iata: 'AMS', city: 'Amsterdam', country: 'NLD', region: 'EU', tz: 'Europe/Amsterdam' },
  MXP: { iata: 'MXP', city: 'Milan', country: 'ITA', region: 'EU', tz: 'Europe/Rome' },
  BCN: { iata: 'BCN', city: 'Barcelona', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  ATH: { iata: 'ATH', city: 'Athens', country: 'GRC', region: 'EU', tz: 'Europe/Athens' },
  ZRH: { iata: 'ZRH', city: 'Zurich', country: 'CHE', region: 'EU', tz: 'Europe/Zurich' },
  // Oceania
  MEL: { iata: 'MEL', city: 'Melbourne', country: 'AUS', region: 'OC', tz: 'Australia/Melbourne' },
  // North America
  ORD: { iata: 'ORD', city: 'Chicago', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  BOS: { iata: 'BOS', city: 'Boston', country: 'USA', region: 'NA', tz: 'America/New_York' },
  YYZ: { iata: 'YYZ', city: 'Toronto', country: 'CAN', region: 'NA', tz: 'America/Toronto' },
  // Africa
  JNB: { iata: 'JNB', city: 'Johannesburg', country: 'ZAF', region: 'AF', tz: 'Africa/Johannesburg' },
  NBO: { iata: 'NBO', city: 'Nairobi', country: 'KEN', region: 'AF', tz: 'Africa/Nairobi' },
  CMN: { iata: 'CMN', city: 'Casablanca', country: 'MAR', region: 'AF', tz: 'Africa/Casablanca' },
  ADD: { iata: 'ADD', city: 'Addis Ababa', country: 'ETH', region: 'AF', tz: 'Africa/Addis_Ababa' },

  // ── Southwest (WN) network — US point-to-point across eight bases, plus the
  // Hawaii ETOPS turns and the Mexico / Caribbean leisure fan ──
  // Bases (the eight biggest, by departures)
  MDW: { iata: 'MDW', city: 'Chicago/Midway', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  LAS: { iata: 'LAS', city: 'Las Vegas', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  DEN: { iata: 'DEN', city: 'Denver', country: 'USA', region: 'NA', tz: 'America/Denver' },
  BWI: { iata: 'BWI', city: 'Baltimore', country: 'USA', region: 'NA', tz: 'America/New_York' },
  DAL: { iata: 'DAL', city: 'Dallas/Love Field', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  PHX: { iata: 'PHX', city: 'Phoenix', country: 'USA', region: 'NA', tz: 'America/Phoenix' },
  HOU: { iata: 'HOU', city: 'Houston/Hobby', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  // West Coast / Pacific
  OAK: { iata: 'OAK', city: 'Oakland', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  SAN: { iata: 'SAN', city: 'San Diego', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  SJC: { iata: 'SJC', city: 'San Jose', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  SMF: { iata: 'SMF', city: 'Sacramento', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  BUR: { iata: 'BUR', city: 'Burbank', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  ONT: { iata: 'ONT', city: 'Ontario', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  SNA: { iata: 'SNA', city: 'Santa Ana', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  RNO: { iata: 'RNO', city: 'Reno', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  SEA: { iata: 'SEA', city: 'Seattle', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  PDX: { iata: 'PDX', city: 'Portland', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  // Mountain West
  SLC: { iata: 'SLC', city: 'Salt Lake City', country: 'USA', region: 'NA', tz: 'America/Denver' },
  ABQ: { iata: 'ABQ', city: 'Albuquerque', country: 'USA', region: 'NA', tz: 'America/Denver' },
  ELP: { iata: 'ELP', city: 'El Paso', country: 'USA', region: 'NA', tz: 'America/Denver' },
  // Texas · Central
  AUS: { iata: 'AUS', city: 'Austin', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  SAT: { iata: 'SAT', city: 'San Antonio', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  OKC: { iata: 'OKC', city: 'Oklahoma City', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  MSY: { iata: 'MSY', city: 'New Orleans', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  STL: { iata: 'STL', city: 'St. Louis', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  MCI: { iata: 'MCI', city: 'Kansas City', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  BNA: { iata: 'BNA', city: 'Nashville', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  MSP: { iata: 'MSP', city: 'Minneapolis', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  MKE: { iata: 'MKE', city: 'Milwaukee', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  // Midwest · Northeast
  DTW: { iata: 'DTW', city: 'Detroit', country: 'USA', region: 'NA', tz: 'America/Detroit' },
  CLE: { iata: 'CLE', city: 'Cleveland', country: 'USA', region: 'NA', tz: 'America/New_York' },
  CMH: { iata: 'CMH', city: 'Columbus', country: 'USA', region: 'NA', tz: 'America/New_York' },
  IND: { iata: 'IND', city: 'Indianapolis', country: 'USA', region: 'NA', tz: 'America/Indiana/Indianapolis' },
  PIT: { iata: 'PIT', city: 'Pittsburgh', country: 'USA', region: 'NA', tz: 'America/New_York' },
  LGA: { iata: 'LGA', city: 'New York/LaGuardia', country: 'USA', region: 'NA', tz: 'America/New_York' },
  DCA: { iata: 'DCA', city: 'Washington/National', country: 'USA', region: 'NA', tz: 'America/New_York' },
  PVD: { iata: 'PVD', city: 'Providence', country: 'USA', region: 'NA', tz: 'America/New_York' },
  BDL: { iata: 'BDL', city: 'Hartford', country: 'USA', region: 'NA', tz: 'America/New_York' },
  // Southeast · Florida · Puerto Rico
  ATL: { iata: 'ATL', city: 'Atlanta', country: 'USA', region: 'NA', tz: 'America/New_York' },
  RDU: { iata: 'RDU', city: 'Raleigh/Durham', country: 'USA', region: 'NA', tz: 'America/New_York' },
  TPA: { iata: 'TPA', city: 'Tampa', country: 'USA', region: 'NA', tz: 'America/New_York' },
  FLL: { iata: 'FLL', city: 'Fort Lauderdale', country: 'USA', region: 'NA', tz: 'America/New_York' },
  JAX: { iata: 'JAX', city: 'Jacksonville', country: 'USA', region: 'NA', tz: 'America/New_York' },
  RSW: { iata: 'RSW', city: 'Fort Myers', country: 'USA', region: 'NA', tz: 'America/New_York' },
  PBI: { iata: 'PBI', city: 'West Palm Beach', country: 'USA', region: 'NA', tz: 'America/New_York' },
  SJU: { iata: 'SJU', city: 'San Juan', country: 'USA', region: 'NA', tz: 'America/Puerto_Rico' },
  // Hawaii (ETOPS overwater)
  HNL: { iata: 'HNL', city: 'Honolulu', country: 'USA', region: 'NA', tz: 'Pacific/Honolulu' },
  OGG: { iata: 'OGG', city: 'Kahului', country: 'USA', region: 'NA', tz: 'Pacific/Honolulu' },
  // ── Advanced Air (AN) network — HHR base in Los Angeles, PHX and ABQ as the
  // gateways, and outstations that are small mountain and coastal towns. OAK,
  // ABQ and PHX are already above; these seven are new ──
  //
  // THREE time zones across ten airports, which is a lot for a carrier this
  // small, so every row names its zone: California is America/Los_Angeles, New
  // Mexico is America/Denver, and ARIZONA DOES NOT OBSERVE DST — PHX is
  // America/Phoenix. For half the year PHX and the California stations share an
  // offset and for the other half they do not, so a stay computed off a fixed
  // offset is wrong six months a year. Never collapse these rows.
  // California — the LA basin and the coast
  HHR: { iata: 'HHR', city: 'Hawthorne', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  CLD: { iata: 'CLD', city: 'Carlsbad', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  // Crescent City sits on the Oregon line, 580 km north of San Francisco, and is
  // the most isolated station in the network: the nearest alternative airport is
  // hours away by road, so a cancellation here cannot be solved by driving.
  CEC: { iata: 'CEC', city: 'Crescent City', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  // Mammoth Lakes — a ski town at 2,170 m in the Sierra Nevada and one of the
  // airline's bases. Winter is both its peak season and the reason it closes.
  MMH: { iata: 'MMH', city: 'Mammoth Lakes', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  // New Mexico — America/Denver, all three high desert or mountain
  AXX: { iata: 'AXX', city: 'Angel Fire', country: 'USA', region: 'NA', tz: 'America/Denver' },
  SVC: { iata: 'SVC', city: 'Silver City', country: 'USA', region: 'NA', tz: 'America/Denver' },
  GUP: { iata: 'GUP', city: 'Gallup', country: 'USA', region: 'NA', tz: 'America/Denver' },
  // ── Air Mauritius (MK) network — MRU hub, an ATR shuttle to Rodrigues, and a
  // long-haul fan to Europe, India, southern Africa and Perth. JNB, NBO, DEL,
  // BOM, MAA, KUL, CDG and LHR already exist above; these eight are new ──
  // Mauritius is UTC+4 all year (Indian/Mauritius, no DST). Rodrigues shares it.
  MRU: { iata: 'MRU', city: 'Mauritius', country: 'MUS', region: 'AF', tz: 'Indian/Mauritius' },
  RRG: { iata: 'RRG', city: 'Rodrigues', country: 'MUS', region: 'AF', tz: 'Indian/Mauritius' },
  // Réunion is FRANCE — an overseas department, EU territory, EU261 applies on
  // departure — but it is 9,000 km from Paris and on the Mauritian clock. The
  // country column says FRA on purpose; the region says AF because that is where
  // the passenger physically is when stranded.
  RUN: { iata: 'RUN', city: 'Saint-Denis (Réunion)', country: 'FRA', region: 'AF', tz: 'Indian/Reunion' },
  TNR: { iata: 'TNR', city: 'Antananarivo', country: 'MDG', region: 'AF', tz: 'Indian/Antananarivo' },
  CPT: { iata: 'CPT', city: 'Cape Town', country: 'ZAF', region: 'AF', tz: 'Africa/Johannesburg' },
  GVA: { iata: 'GVA', city: 'Geneva', country: 'CHE', region: 'EU', tz: 'Europe/Zurich' },
  LGW: { iata: 'LGW', city: 'London/Gatwick', country: 'GBR', region: 'EU', tz: 'Europe/London' },
  PER: { iata: 'PER', city: 'Perth', country: 'AUS', region: 'OC', tz: 'Australia/Perth' },
  // Mexico · Central America · Caribbean
  CUN: { iata: 'CUN', city: 'Cancún', country: 'MEX', region: 'CB', tz: 'America/Cancun' },
  SJD: { iata: 'SJD', city: 'Los Cabos', country: 'MEX', region: 'CB', tz: 'America/Mazatlan' },
  PVR: { iata: 'PVR', city: 'Puerto Vallarta', country: 'MEX', region: 'CB', tz: 'America/Mexico_City' },
  MBJ: { iata: 'MBJ', city: 'Montego Bay', country: 'JAM', region: 'CB', tz: 'America/Jamaica' },
  NAS: { iata: 'NAS', city: 'Nassau', country: 'BHS', region: 'CB', tz: 'America/Nassau' },
  SJO: { iata: 'SJO', city: 'San José', country: 'CRI', region: 'CB', tz: 'America/Costa_Rica' },

  // ── Iberia (IB) network — MAD hub; the Latin-American fan and most European
  // gateways were already here for LATAM / Etihad / Southwest ──
  // Spain beyond Madrid (Canaries keep their own zone)
  AGP: { iata: 'AGP', city: 'Málaga', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  PMI: { iata: 'PMI', city: 'Palma', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  SVQ: { iata: 'SVQ', city: 'Seville', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  VLC: { iata: 'VLC', city: 'Valencia', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  BIO: { iata: 'BIO', city: 'Bilbao', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  ALC: { iata: 'ALC', city: 'Alicante', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  SCQ: { iata: 'SCQ', city: 'Santiago', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  VGO: { iata: 'VGO', city: 'Vigo', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  IBZ: { iata: 'IBZ', city: 'Ibiza', country: 'ESP', region: 'EU', tz: 'Europe/Madrid' },
  LPA: { iata: 'LPA', city: 'Las Palmas', country: 'ESP', region: 'EU', tz: 'Atlantic/Canary' },
  TFN: { iata: 'TFN', city: 'Tenerife', country: 'ESP', region: 'EU', tz: 'Atlantic/Canary' },
  ACE: { iata: 'ACE', city: 'Lanzarote', country: 'ESP', region: 'EU', tz: 'Atlantic/Canary' },
  // Europe
  OPO: { iata: 'OPO', city: 'Porto', country: 'PRT', region: 'EU', tz: 'Europe/Lisbon' },
  ORY: { iata: 'ORY', city: 'Paris/Orly', country: 'FRA', region: 'EU', tz: 'Europe/Paris' },
  BRU: { iata: 'BRU', city: 'Brussels', country: 'BEL', region: 'EU', tz: 'Europe/Brussels' },
  DUS: { iata: 'DUS', city: 'Düsseldorf', country: 'DEU', region: 'EU', tz: 'Europe/Berlin' },
  BER: { iata: 'BER', city: 'Berlin', country: 'DEU', region: 'EU', tz: 'Europe/Berlin' },
  VIE: { iata: 'VIE', city: 'Vienna', country: 'AUT', region: 'EU', tz: 'Europe/Vienna' },
  CPH: { iata: 'CPH', city: 'Copenhagen', country: 'DNK', region: 'EU', tz: 'Europe/Copenhagen' },
  ARN: { iata: 'ARN', city: 'Stockholm', country: 'SWE', region: 'EU', tz: 'Europe/Stockholm' },
  NAP: { iata: 'NAP', city: 'Naples', country: 'ITA', region: 'EU', tz: 'Europe/Rome' },
  PRG: { iata: 'PRG', city: 'Prague', country: 'CZE', region: 'EU', tz: 'Europe/Prague' },
  WAW: { iata: 'WAW', city: 'Warsaw', country: 'POL', region: 'EU', tz: 'Europe/Warsaw' },
  EDI: { iata: 'EDI', city: 'Edinburgh', country: 'GBR', region: 'EU', tz: 'Europe/London' },
  // North Africa · Middle East
  TNG: { iata: 'TNG', city: 'Tangier', country: 'MAR', region: 'AF', tz: 'Africa/Casablanca' },
  RAK: { iata: 'RAK', city: 'Marrakesh', country: 'MAR', region: 'AF', tz: 'Africa/Casablanca' },
  ALG: { iata: 'ALG', city: 'Algiers', country: 'DZA', region: 'AF', tz: 'Africa/Algiers' },
  TLV: { iata: 'TLV', city: 'Tel Aviv', country: 'ISR', region: 'ME', tz: 'Asia/Jerusalem' },
  // Mexico · Central America · Caribbean
  PTY: { iata: 'PTY', city: 'Panama City', country: 'PAN', region: 'CB', tz: 'America/Panama' },
  SAL: { iata: 'SAL', city: 'San Salvador', country: 'SLV', region: 'CB', tz: 'America/El_Salvador' },
  GUA: { iata: 'GUA', city: 'Guatemala City', country: 'GTM', region: 'CB', tz: 'America/Guatemala' },
  HAV: { iata: 'HAV', city: 'Havana', country: 'CUB', region: 'CB', tz: 'America/Havana' },
  SDQ: { iata: 'SDQ', city: 'Santo Domingo', country: 'DOM', region: 'CB', tz: 'America/Santo_Domingo' },
  MEX: { iata: 'MEX', city: 'Mexico City', country: 'MEX', region: 'CB', tz: 'America/Mexico_City' },
  MTY: { iata: 'MTY', city: 'Monterrey', country: 'MEX', region: 'CB', tz: 'America/Monterrey' },
  // North America
  SFO: { iata: 'SFO', city: 'San Francisco', country: 'USA', region: 'NA', tz: 'America/Los_Angeles' },
  DFW: { iata: 'DFW', city: 'Dallas', country: 'USA', region: 'NA', tz: 'America/Chicago' },
  IAD: { iata: 'IAD', city: 'Washington/Dulles', country: 'USA', region: 'NA', tz: 'America/New_York' },

  // ── Plus Ultra (PU) network — MAD hub, TFN second base, a South-American and
  // Caribbean long-haul fan. MAD, TFN, LIM, EZE, BOG, CTG and MVD were already
  // here for LATAM / Iberia; only the three below are new ──
  // Venezuela — permanent UTC−4, no DST (the 2016 move back from UTC−4:30 is
  // history the IANA zone already carries).
  CCS: { iata: 'CCS', city: 'Caracas', country: 'VEN', region: 'VE', tz: 'America/Caracas' },
  // VLN is Valencia **VENEZUELA** (Arturo Michelena Intl), NOT Valencia Spain —
  // that one is VLC, region EU, Europe/Madrid, ~40 rows above. Two cities called
  // Valencia, two IATA codes, two continents: never merge these rows.
  VLN: { iata: 'VLN', city: 'Valencia', country: 'VEN', region: 'VE', tz: 'America/Caracas' },
  // Brazil's north-east: UTC−3 like São Paulo, but its own zone. Brazil dropped
  // DST nationally in 2019 and the north-east never observed it even before.
  NAT: { iata: 'NAT', city: 'Natal', country: 'BRA', region: 'BR', tz: 'America/Fortaleza' },
});

/** Human-friendly region labels, keyed by {@link RegionCode}. */
export const REGION_LABELS: Readonly<Record<RegionCode, string>> = Object.freeze({
  CL: 'Chile',
  PE: 'Peru',
  BR: 'Brazil',
  CO: 'Colombia',
  AR: 'Argentina',
  UY: 'Uruguay',
  PY: 'Paraguay',
  EC: 'Ecuador',
  VE: 'Venezuela',
  EU: 'Europe',
  NA: 'North America',
  OC: 'Oceania',
  ME: 'Middle East',
  AS: 'Asia',
  AF: 'Africa',
  CB: 'Mexico, Central America & the Caribbean',
});

/** Human-friendly country labels, keyed by ISO 3166-1 alpha-3. */
export const COUNTRY_LABELS: Readonly<Record<string, string>> = Object.freeze({
  CHL: 'Chile',
  PER: 'Peru',
  BRA: 'Brazil',
  COL: 'Colombia',
  ARG: 'Argentina',
  URY: 'Uruguay',
  PRY: 'Paraguay',
  ECU: 'Ecuador',
  VEN: 'Venezuela',
  ESP: 'Spain',
  PRT: 'Portugal',
  ITA: 'Italy',
  USA: 'United States',
  NZL: 'New Zealand',
  AUS: 'Australia',
  ARE: 'United Arab Emirates',
  QAT: 'Qatar',
  BHR: 'Bahrain',
  KWT: 'Kuwait',
  OMN: 'Oman',
  SAU: 'Saudi Arabia',
  JOR: 'Jordan',
  LBN: 'Lebanon',
  EGY: 'Egypt',
  TUR: 'Türkiye',
  IND: 'India',
  PAK: 'Pakistan',
  LKA: 'Sri Lanka',
  BGD: 'Bangladesh',
  NPL: 'Nepal',
  MDV: 'Maldives',
  GBR: 'United Kingdom',
  IRL: 'Ireland',
  FRA: 'France',
  DEU: 'Germany',
  NLD: 'Netherlands',
  GRC: 'Greece',
  CHE: 'Switzerland',
  THA: 'Thailand',
  MYS: 'Malaysia',
  SGP: 'Singapore',
  IDN: 'Indonesia',
  PHL: 'Philippines',
  HKG: 'Hong Kong',
  KOR: 'South Korea',
  JPN: 'Japan',
  CHN: 'China',
  CAN: 'Canada',
  ZAF: 'South Africa',
  KEN: 'Kenya',
  MAR: 'Morocco',
  ETH: 'Ethiopia',
  MEX: 'Mexico',
  JAM: 'Jamaica',
  BHS: 'Bahamas',
  CRI: 'Costa Rica',
  BEL: 'Belgium',
  AUT: 'Austria',
  DNK: 'Denmark',
  SWE: 'Sweden',
  CZE: 'Czechia',
  POL: 'Poland',
  DZA: 'Algeria',
  ISR: 'Israel',
  PAN: 'Panama',
  SLV: 'El Salvador',
  GTM: 'Guatemala',
  CUB: 'Cuba',
  DOM: 'Dominican Republic',
});
